import { Body1, Button, Input, Label, Tooltip } from '@fluentui/react-components';
import { useContext, useEffect, useId, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { ToastContext } from '@shared/contexts/ToastContext.context';
import toPositiveInteger from '../helpers/toPositiveInteger';

type TProps = {
  total: number;
  page: number;
  pageSize: number;
  showTotal?: boolean;
  maxPageSize?: number;
  isFetching?: boolean;
  ariaLabel?: string;
  deferPageSizeChange?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
};

const PAGE_SIZE_CHANGE_DELAY_MS = 300;

const parsePageSizeDraft = (value: string): number | undefined => {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 1 ? parsed : undefined;
};

const PaginationControls = ({
  total,
  page: requestedPage,
  pageSize: requestedPageSize,
  showTotal = false,
  maxPageSize,
  isFetching = false,
  ariaLabel = 'Pagination',
  deferPageSizeChange = false,
  onPageChange,
  onPageSizeChange,
}: TProps) => {
  const toast = useContext(ToastContext);
  const pageSizeInputId = useId();
  const pageSize = toPositiveInteger(requestedPageSize, 1);
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [pageSize, total]);
  const page = Math.min(toPositiveInteger(requestedPage, 1), totalPages);
  const [pageSizeDraft, setPageSizeDraft] = useState(String(pageSize));
  const applyPageSize = (value: number) => {
    const next = maxPageSize === undefined ? value : Math.min(value, maxPageSize);
    if (next !== value) setPageSizeDraft(String(next));
    onPageSizeChange(next);
  };
  const debouncedPageSizeChange = useDebouncedCallback(applyPageSize, PAGE_SIZE_CHANGE_DELAY_MS);

  useEffect(() => {
    debouncedPageSizeChange.cancel();
    setPageSizeDraft(String(pageSize));
  }, [debouncedPageSizeChange, pageSize]);

  useEffect(() => () => debouncedPageSizeChange.cancel(), [debouncedPageSizeChange]);

  const handleCopyTotal = async () => {
    try {
      await navigator.clipboard.writeText(String(total));
      toast?.show('success', 'Total copied to clipboard.');
    } catch (error) {
      console.error('Copy failed', error);
      toast?.show('error', 'Copying the total failed!');
    }
  };

  const handlePageChange = (nextPage: number) => onPageChange(Math.min(Math.max(nextPage, 1), totalPages));

  const handlePageSizeChange = (value: string) => {
    setPageSizeDraft(value);
    const parsed = parsePageSizeDraft(value);
    if (parsed === undefined) {
      debouncedPageSizeChange.cancel();
      return;
    }
    if (deferPageSizeChange) debouncedPageSizeChange(parsed);
    else applyPageSize(parsed);
  };

  const commitPageSizeDraft = () => {
    const parsed = parsePageSizeDraft(pageSizeDraft);
    debouncedPageSizeChange.cancel();
    if (parsed === undefined) {
      setPageSizeDraft(String(pageSize));
      return;
    }
    if (deferPageSizeChange) applyPageSize(parsed);
  };

  return (
    <nav className="flex justify-between items-center pt-4 px-3" aria-label={ariaLabel}>
      <div className="flex items-center space-x-2">
        <Label htmlFor={pageSizeInputId}>{showTotal ? 'Showing' : 'Items per page:'}</Label>
        <Input
          id={pageSizeInputId}
          aria-label="Items per page"
          type="number"
          value={pageSizeDraft}
          min={1}
          max={maxPageSize}
          onChange={(_, data) => handlePageSizeChange(data.value)}
          onBlur={commitPageSizeDraft}
          onKeyDown={(event) => {
            if (event.key === 'Enter') commitPageSizeDraft();
          }}
          className="w-20"
        />
        {showTotal ? (
          <>
            <Body1>of</Body1>
            <Tooltip content="Click to copy." relationship="description" withArrow showDelay={500}>
              <Button appearance="secondary" className="!min-w-0" onClick={handleCopyTotal}>
                {total}
              </Button>
            </Tooltip>
          </>
        ) : null}
      </div>

      <div className="flex items-center space-x-3">
        <Button appearance="subtle" disabled={isFetching || page <= 1} onClick={() => handlePageChange(page - 1)}>
          Previous
        </Button>
        <Body1>
          Page {page} of {totalPages}
        </Body1>
        <Button
          appearance="subtle"
          disabled={isFetching || page >= totalPages}
          onClick={() => handlePageChange(page + 1)}
        >
          Next
        </Button>
      </div>
    </nav>
  );
};

export default PaginationControls;
